/**
 * 이상한 방 - 걸기 칩 (HUD 배수 칩 옆의 단추 하나, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · 4.5절(점수) · 5절(3번 - 말싸움을 규칙으로)
 *            `gameHud.css` 머리말(화면 중앙은 비운다 · 누르는 것은 44px · 색은 `--gh-*`)
 *            로드맵 M7(`raiseChip` - HUD 배수 칩 옆. 누르면 두 겹 테두리 · 다음 발견 자막이 두 배 점수)
 *
 * ## 이 칩은 조건을 계산하지 않는다
 *
 * 걸 수 있는가(잔탄 ≥ `raiseMinAmmo` · 한 판에 한 번)는 `game/raise.canRaise`의 것이고, 두 리듀서가 그 답을 뷰의
 * 불리언(`GameHudView.canRaise` · `raised`)으로 내린다. 칩은 그 둘을 읽어 얼굴만 바꾼다. 여기서 `ammo >= 4`를 적기 시작하면
 * `?raiseMin=`을 돌린 날 칩의 얼굴과 리듀서의 거부가 갈리고, 그 순간 이 단추는 "눌리는데 아무 일도 없는" 물건이 된다
 * (`gameHud.ts`의 탄창 칩과 같은 규약).
 *
 * ## 잠긴 칩은 사라지지 않고 이유를 말한다
 *
 * 잠금은 `disabled`가 아니라 `aria-disabled`다. `disabled`는 클릭 자체를 삼켜서 "왜 안 되는지"를 말할 기회가 없어진다 -
 * 눌리지 않는 버튼은 고장 난 버튼과 구별되지 않는다(GLOSSARY 3절). 그래서 잠긴 칩을 누르면 `onPress`가 아니라
 * `onLocked(reason)`가 불리고, HUD가 그 이유를 토스트로 띄운다. 이유는 둘뿐이다 - 이미 걸었다 / 탄환이 모자라다.
 * 둘을 가르는 것은 `raised` 하나다(`canRaise`가 거짓인데 `raised`도 거짓이면 남은 이유는 잔탄뿐이다 - 이건 규칙의 재계산이
 * 아니라 뷰 두 값의 대응이다). 이 대응이 참인 것은 **칩이 서 있는 사람이 술래**일 때뿐이다 - 다녀간 사람·관전자의 `canRaise` 거짓은
 * 역할 때문이라, HUD는 그들에게 칩을 세우지 않는다(`GameHudView.raiseVisible`). 칩이 이유를 유도하는 자리에 역할이 오면 거짓 이유가 된다.
 *
 * ## 자리를 비우지 않는다
 *
 * 콤보 칩과 같은 줄에 서고, 수색이 아닐 때는 `visibility`로만 숨는다. 흐름에서 넣었다 뺐다 하면 아래 건너뛰기 버튼이
 * 위아래로 움직이고, 움직이는 버튼은 "눌렀는데 엉뚱한 게 눌리는" 실패로 가는 길이다(`gameHud.ts`의 `createFoundBlock` 주석).
 *
 * ## 두 겹 테두리 = 걸었다
 *
 * 선언 뒤의 칩은 노란 판에 먹색 글자가 되고 바깥에 노란 고리가 한 겹 더 선다(`raiseChip.css`의 `is-on`). 콤보 칩의 천장
 * (`.gh-combo.is-cap`)과 같은 뒤집기 문법이라 "지금 배수가 붙어 있다"가 안 읽어도 보인다. 그 위에 글자를 더하지 않는다.
 */

import { RAISE_MULTIPLIER } from '../game/raise'
import { t } from '../i18n'
import type { MessageKey } from '../i18n/ko'

import { createElement, setAttr, setFlag, setText } from './dom'
/*
 * 배수 표기(`×2.0`)는 콤보 칩·발견 자막과 **같은 함수**다. 값은 `game/raise.RAISE_MULTIPLIER` 하나에서 온다 -
 * 여기 '×2'를 글자로 박으면 배수가 바뀐 날 칩만 옛 값을 말한다.
 */
import { formatMultiplier } from './juice'

import './raiseChip.css'

/* --- 타입 ---------------------------------------------------------------------- */

/** 칩이 그리는 데 필요한 전부. 셋 다 뷰에서 그대로 온다 - 여기서 하나라도 유도하면 규칙이 두 벌이 된다. */
export type RaiseChipView = Readonly<{
  /** 걸기를 선언했는가(`GameHudView.raised`). 두 겹 테두리의 근거다. */
  raised: boolean
  /** 지금 걸 수 있는가(`GameHudView.canRaise`). 거짓이면 잠긴다. */
  canRaise: boolean
  /** 칩이 서는 시간인가(수색 중). 아니면 자리만 남기고 숨는다. */
  active: boolean
}>

/** 잠긴 이유. 값이 곧 사전 키의 마지막 토막이다(`raise.lock.<reason>`). */
export type RaiseLockReason = 'already' | 'ammo'

/**
 * `RaiseLockReason`의 값 전부. `i18n/index.test.ts`가 이 목록으로 "조립 키가 두 사전에 있는가"를 돈다 - 타입은 런타임에 열거할 수
 * 없으므로 목록이 하나 더 필요하고, `satisfies`가 목록과 타입이 갈리는 것을 막는다(`gameHud.HUD_PHASES`와 같은 장치).
 */
export const RAISE_LOCK_REASONS = ['already', 'ammo'] as const satisfies readonly RaiseLockReason[]

export type RaiseChipOptions = Readonly<{
  /** 걸 수 있는 상태에서 눌렀다. 리듀서에 `raise`를 보내는 것은 부르는 쪽의 몫이다 - 거부 판단은 여기서 하지 않는다. */
  onPress: () => void
  /** 잠긴 칩을 눌렀다. 이유를 화면에 띄우는 것은 부르는 쪽(HUD의 토스트)의 몫이다. */
  onLocked: (reason: RaiseLockReason) => void
}>

export type RaiseChipHandle = Readonly<{
  root: HTMLButtonElement
  render: (view: RaiseChipView) => void
  dispose: () => void
}>

/* --- 순수 조각 --------------------------------------------------------------------- */

/**
 * 뷰 → 잠긴 이유. 걸 수 있으면 `null`. 이미 걸었으면 그것이 이유고, 아니면 남은 조건은 잔탄 하나다(`raise.canRaise`의 두 조건).
 * 리듀서의 비교식을 베끼는 것이 아니라 **뷰 두 값의 대응표**다 - 조건이 셋으로 늘면 뷰에 값이 하나 더 와야 한다.
 */
export function lockReasonOf(view: Pick<RaiseChipView, 'raised' | 'canRaise'>): RaiseLockReason | null {
  if (view.canRaise) return null
  return view.raised ? 'already' : 'ammo'
}

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 칩을 만든다. 부모에 붙이는 것은 호출자(`gameHud.ts`)의 몫이다 - 콤보 칩과 한 줄에 서야 하므로 자리는 HUD가 정한다.
 * 처음에는 숨은 상태(`active` 거짓)다 - 첫 뷰가 오기 전에 눌릴 수 있는 칩이 있으면 안 된다.
 */
export function createRaiseChip(options: RaiseChipOptions): RaiseChipHandle {
  const root = createElement('button', 'rc-root is-off')
  root.type = 'button'

  /* 배수 표기는 글자가 아니라 칩의 신원이다 - 라벨이 '걸기'→'걸었다'로 바뀌어도 배수는 그대로 서 있다. */
  const mult = createElement('span', 'rc-mult', formatMultiplier(RAISE_MULTIPLIER))
  mult.setAttribute('aria-hidden', 'true')
  const label = createElement('span', 'rc-label', '')
  root.append(mult, label)

  /** 마지막으로 그린 뷰. 클릭이 "지금 잠겼는가"를 물을 유일한 자리다 - HUD와 같은 배선(`lastView`). */
  let lastView: RaiseChipView | null = null

  function onClick(): void {
    if (lastView === null || !lastView.active) return
    const reason = lockReasonOf(lastView)
    if (reason === null) options.onPress()
    else options.onLocked(reason)
  }
  root.addEventListener('click', onClick)

  return {
    root,

    render(view: RaiseChipView): void {
      lastView = view
      const reason = lockReasonOf(view)

      setFlag(root, 'is-off', !view.active)
      setFlag(root, 'is-on', view.raised)
      setFlag(root, 'is-locked', reason !== null)
      // 숨은 칩은 보조기술에서도 없다. 잠긴 칩은 남되 `aria-disabled` - 눌리면 이유를 말해야 하므로 `disabled`가 아니다(머리말).
      setAttr(root, 'aria-hidden', view.active ? 'false' : 'true')
      setAttr(root, 'aria-disabled', reason === null ? 'false' : 'true')
      setAttr(root, 'aria-pressed', view.raised ? 'true' : 'false')

      setText(label, t(view.raised ? 'raise.chip.on' : 'raise.chip.label'))
      const ariaKey: MessageKey = view.raised ? 'raise.chip.onAria' : 'raise.chip.aria'
      setAttr(
        root,
        'aria-label',
        reason === null || view.raised ? t(ariaKey) : t('raise.chip.lockedAria', { reason: t(`raise.lock.${reason}`) }),
      )
    },

    dispose(): void {
      root.removeEventListener('click', onClick)
      root.remove()
    },
  }
}
